<template>
    <div class="search-block">
        <VJoyPanel>
            <template v-if="title" #panel-title>
                {{ title }}
            </template>
            <template #panel-body>
                <slot name="header"></slot>
                <form novalidate @submit.prevent="onSubmit">
                    <div class="search-input">
                        <AutoComplete
                            ref="queryRef"
                            v-model="query"
                            class="search-input__autocomplete"
                            :suggestions="querySuggestions"
                            :aria-label="$t('search.engine.label')"
                            :placeholder="$t('search.engine.label')"
                            :empty-search-message="$t('generic.no.results.found')"
                            :auto-option-focus="false"
                            :complete-on-focus="false"
                            :min-length="2"
                            :delay="100"
                            data-testid="search-block-query-field"
                            show-clear
                            @keydown.tab="onQueryTab"
                            @complete="querySearch"
                        >
                            <template #clearicon>
                                <VJoyIcon size="xsmall" name="cross" class="p-autocomplete-clear-icon" @click="clearQuery" />
                            </template>
                        </AutoComplete>
                        <VJoyIcon size="xsmall" name="search" />
                    </div>
                    <VJoyFormError v-if="submitted && !query">
                        {{ $t('search.engine.validation.message') }}
                    </VJoyFormError>
                    <MaltLocationAutocomplete
                        v-model="selectedLocation"
                        class="joy-mt-4"
                        input-test-id="search-block-location-field"
                        :label="locationInputLabel"
                        :layer="[
                            {name: 'LOCALITY', size: 5},
                            {name: 'COUNTRY', size: 2},
                        ]"
                        :remote-allowed="true"
                        :remote-by-country="true"
                        :remote-label="remoteLabel"
                        placeholder-icon-color="secondary"
                    />

                    <!-- @slot Customize the submit CTA -->
                    <slot name="search-block-submit">
                        <VJoyButton class="search-block__submit joy-mt-4" variant="main" type="submit" data-testid="search-block-btn-submit">
                            {{ $t('main.search.cta') }}
                        </VJoyButton>
                    </slot>
                    <span v-if="displayProjectProposal" class="search-block__project-link">
                        {{ $t('home.banner.proposal.project.or') }}
                        <VJoyLink :href="`/s/advanced?brief_origin=search_block_${page}`">
                            {{ $t('home.banner.proposal.project') }}
                        </VJoyLink>
                    </span>
                </form>
                <div v-if="displayReassurance" class="reassurance">
                    <span class="reassurance__link">
                        <a :href="'https://help.malt.com/kb/guide/' + languageCode + '/whats-an-escrow-account-HaQeig5r1D/Steps/68764'">
                            <VJoyIcon name="lock" size="small" />
                        </a>
                        {{ $t('home.re.assurances.links.secure') }}
                    </span>
                    <span class="reassurance__link">
                        <a :href="$t('home.re.assurances.links.url')">
                            <MaltImg :src="$t('home.re.assurances.links.logo.url')" alt="Axa" width="25" height="25" />
                        </a>
                        {{ $t('home.re.assurances.links.axa') }}
                    </span>
                    <span class="reassurance__link">
                        <a
                            :href="
                                'https://help.malt.com/kb/guide/' +
                                languageCode +
                                '/how-do-you-verify-that-a-freelancer-is-in-good-legal-standing-due-diligence-YY37HwW9IZ/Steps/68671'
                            "
                        >
                            <VJoyIcon name="legal-check" size="small" />
                        </a>
                        {{ $t('home.re.assurances.links.data') }}
                    </span>
                </div>
            </template>
        </VJoyPanel>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, watch} from 'vue';
    import {MaltImg} from '#components';
    import {useLocale, useTranslation, useGlobalMatching} from '#imports';
    import {VJoyPanel, VJoyIcon, VJoyFormError, VJoyLink, VJoyButton} from '@maltjoy/core-vue';
    import {useQueryAutocomplete} from '../../../composables/useQueryAutocomplete';
    import {getSearchURL} from '../../../internals/utils/search';
    import type {Location} from '../../../types/location';
    import MaltLocationAutocomplete from '../MaltLocationAutocomplete.vue';

    const props = defineProps({
        /**
         * The page's name where this component is included
         */
        page: {
            type: String,
            required: true,
        },
        /** sets a title for the VJoyPanel */
        title: {
            type: String,
            required: false,
        },
        /** display reassurance items at the bottom */
        displayReassurance: {
            type: Boolean,
            default: true,
        },
        /** display project proposal link next to form submit */
        displayProjectProposal: {
            type: Boolean,
            default: true,
        },
    });

    const {locale} = useLocale();
    const [languageCode] = locale.value?.split('-') ?? [];
    const {t} = useTranslation();

    const {isBrowsingGlobalMatching} = useGlobalMatching();

    const {query, sanitizedQuery, queryRef, querySuggestions, onQueryTab, querySearch, clearQuery} = useQueryAutocomplete();

    const submitted = ref(false);
    const selectedLocation = ref<Location | null>(null);

    const remoteLabel = computed(() => (isBrowsingGlobalMatching.value ? t('search.location.remote.anywhere') : undefined));

    const locationInputLabel = computed(() =>
        isBrowsingGlobalMatching.value
            ? t('search.location.com.label')
            : t('search.advanced.ai.entrypoint.mode.keyword.search.input.location.placeholder'),
    );

    watch(query, () => {
        if (submitted.value) {
            submitted.value = false;
        }
    });

    function onSubmit() {
        submitted.value = true;
        if (!query.value) {
            return;
        }
        const sourceComponent = `search_block_${props.page}`;
        window.location.href = getSearchURL(sanitizedQuery.value, selectedLocation.value, sourceComponent);
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '../../../internals/style/_search-input';

    .search-block {
        max-width: 700px;
        margin-bottom: var(--joy-core-spacing-12);

        &__project-link {
            display: inline-block;
            margin-left: var(--joy-core-spacing-4);
        }

        @include mq.screen_xs {
            max-width: 100%;

            &__submit {
                width: 100%;
            }

            &__project-link {
                width: 100%;
                text-align: center;
                margin: 0;
                margin-top: var(--joy-core-spacing-2);
            }
        }
    }

    .reassurance {
        font-size: var(--joy-font-size-primary-300);
        display: flex;

        &__link {
            display: flex;
            align-items: center;

            &:not(:last-child) {
                margin-right: var(--joy-core-spacing-8);
            }

            > a {
                margin-right: var(--joy-core-spacing-3);
            }
        }

        img {
            border-radius: var(--joy-core-radius-3);
        }

        @include mq.screen_xs {
            flex-direction: column;

            &__link {
                margin-bottom: var(--joy-core-spacing-3);
            }
        }
    }
</style>
